import { useState, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import { TrendingUp, BarChart3, Eye, Target } from 'lucide-react'
import { useApi, asArray } from '../../hooks/useApi'

interface MetricsEntry {
  date: string
  avgPosition: number
  trafficForecast: number
  searchVisibility: number
  topTenPercent: number
  keywordCount: number
}

interface EvolutionChartProps {
  projectId: string
}

type MetricTab = 'avgPosition' | 'trafficForecast' | 'searchVisibility' | 'topTenPercent'
type Period = '7j' | '1M' | '3M' | '6M' | '1A' | '2A'
type Grouping = 'day' | 'week' | 'month'

const TABS: { key: MetricTab; label: string; icon: typeof TrendingUp; color: string; gradient: string }[] = [
  { key: 'avgPosition', label: 'Position moyenne', icon: TrendingUp, color: '#6366F1', gradient: 'rgba(99,102,241,0.15)' },
  { key: 'trafficForecast', label: 'Prevision trafic', icon: BarChart3, color: '#10B981', gradient: 'rgba(16,185,129,0.15)' },
  { key: 'searchVisibility', label: 'Visibilite', icon: Eye, color: '#F59E0B', gradient: 'rgba(245,158,11,0.15)' },
  { key: 'topTenPercent', label: '% TOP 10', icon: Target, color: '#8B5CF6', gradient: 'rgba(139,92,246,0.15)' },
]

const PERIODS: { key: Period; label: string; days: number }[] = [
  { key: '7j', label: '7j', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '6M', label: '6M', days: 180 },
  { key: '1A', label: '1A', days: 365 },
  { key: '2A', label: '2A', days: 730 },
]

const GROUPINGS: { key: Grouping; label: string }[] = [
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
]

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0]
}

export default function EvolutionChart({ projectId }: EvolutionChartProps) {
  const [activeTab, setActiveTab] = useState<MetricTab>('avgPosition')
  const [period, setPeriod] = useState<Period>('1M')
  const [grouping, setGrouping] = useState<Grouping>('day')

  const { from, to } = useMemo(() => {
    const toDate = new Date()
    const fromDate = new Date()
    const days = PERIODS.find(p => p.key === period)?.days ?? 30
    fromDate.setDate(toDate.getDate() - days)
    return { from: formatDate(fromDate), to: formatDate(toDate) }
  }, [period])

  const { data: metricsResponse, loading } = useApi<{ data: MetricsEntry[] } | MetricsEntry[]>(
    `/api/metrics/clients/${projectId}?from=${from}&to=${to}&group=${grouping}`
  )

  const metrics = useMemo(() => {
    if (!metricsResponse) return []
    // Handle both { data: [...] } and [...] formats
    if (Array.isArray(metricsResponse)) return metricsResponse
    if ('data' in metricsResponse && Array.isArray(metricsResponse.data)) return metricsResponse.data
    return []
  }, [metricsResponse])

  const tab = TABS.find(t => t.key === activeTab)!

  const option = useMemo(() => {
    if (metrics.length === 0) return null

    const dates = metrics.map(m => {
      const dt = new Date(m.date)
      if (grouping === 'month') return dt.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' })
      if (grouping === 'week') return dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      return dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
    })

    const values = metrics.map(m => m[activeTab])
    const isInverse = activeTab === 'avgPosition'
    const isPercent = activeTab === 'searchVisibility' || activeTab === 'topTenPercent'

    return {
      grid: { left: 56, right: 16, top: 16, bottom: 32 },
      xAxis: {
        type: 'category' as const,
        data: dates,
        axisLine: { lineStyle: { color: '#E2E4ED' } },
        axisLabel: { color: '#8B8FA7', fontSize: 10 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        inverse: isInverse,
        min: isInverse ? 1 : isPercent ? 0 : undefined,
        max: isPercent ? 100 : undefined,
        axisLine: { show: false },
        axisLabel: {
          color: '#8B8FA7',
          fontSize: 10,
          formatter: (v: number) => {
            if (isInverse) return `#${v}`
            if (isPercent) return `${v}%`
            return v.toLocaleString('fr-FR')
          },
        },
        splitLine: { lineStyle: { color: '#F4F5FA', type: 'dashed' as const } },
      },
      series: [
        {
          type: 'line',
          data: values,
          smooth: 0.4,
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { color: tab.color, width: 2.5 },
          itemStyle: { color: tab.color, borderColor: '#fff', borderWidth: 2 },
          areaStyle: {
            color: {
              type: 'linear' as const,
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: tab.gradient },
                { offset: 1, color: 'rgba(255,255,255,0)' },
              ],
            },
          },
        },
      ],
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: '#1B2559',
        borderColor: '#1B2559',
        borderRadius: 8,
        padding: [8, 12],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params
          let label = ''
          if (activeTab === 'avgPosition') label = `Position moyenne : <b style="color:${tab.color}">#${p.value}</b>`
          else if (activeTab === 'trafficForecast') label = `Trafic estime : <b style="color:${tab.color}">${Number(p.value).toLocaleString('fr-FR')} clics</b>`
          else if (activeTab === 'searchVisibility') label = `Visibilite : <b style="color:${tab.color}">${p.value}%</b>`
          else label = `TOP 10 : <b style="color:${tab.color}">${p.value}%</b>`
          return `<span style="color:#8B8FA7">${p.name}</span><br/>${label}`
        },
      },
    }
  }, [metrics, activeTab, grouping, tab])

  const lastValue = metrics.length > 0 ? metrics[metrics.length - 1][activeTab] : null

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      {/* Header : tabs + selectors */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        {/* Metric tabs */}
        <div className="flex gap-1">
          {TABS.map(t => {
            const Icon = t.icon
            const isActive = activeTab === t.key
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? 'text-white'
                    : 'text-[#8B8FA7] hover:text-[#555A73] hover:bg-[#F4F5FA]'
                }`}
                style={isActive ? { backgroundColor: t.color } : {}}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.25} />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* Period + grouping selectors */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-[#F4F5FA] rounded-lg p-0.5">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  period === p.key
                    ? 'bg-white text-[#1B2559] shadow-sm'
                    : 'text-[#8B8FA7] hover:text-[#555A73]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="w-px h-5 bg-[#E2E4ED]" />
          <div className="flex items-center gap-0.5 bg-[#F4F5FA] rounded-lg p-0.5">
            {GROUPINGS.map(g => (
              <button
                key={g.key}
                onClick={() => setGrouping(g.key)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  grouping === g.key
                    ? 'bg-white text-[#1B2559] shadow-sm'
                    : 'text-[#8B8FA7] hover:text-[#555A73]'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="flex items-center justify-center h-[260px]">
          <div className="animate-pulse rounded-xl bg-[#E2E4ED] w-full h-full" />
        </div>
      ) : !option || metrics.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[260px]">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#F4F5FA] mb-3">
            <TrendingUp className="w-5 h-5 text-[#8B8FA7]" strokeWidth={1.25} />
          </div>
          <p className="text-sm text-[#8B8FA7]">Aucune donnee historique</p>
          <p className="text-xs text-[#B0B4C5] mt-0.5">Les donnees apparaitront apres les verifications quotidiennes</p>
        </div>
      ) : (
        <ReactECharts option={option} style={{ height: 260 }} />
      )}

      {/* Current value footer */}
      {lastValue !== null && metrics.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#F4F5FA] flex items-center gap-2">
          <span className="text-xs text-[#8B8FA7]">Actuel :</span>
          <span className="text-sm font-semibold" style={{ color: tab.color }}>
            {activeTab === 'avgPosition' && `#${lastValue}`}
            {activeTab === 'trafficForecast' && `${Number(lastValue).toLocaleString('fr-FR')} clics/mois`}
            {activeTab === 'searchVisibility' && `${lastValue}%`}
            {activeTab === 'topTenPercent' && `${lastValue}%`}
          </span>
          <span className="text-[11px] text-[#B0B4C5]">
            ({metrics.length} point{metrics.length > 1 ? 's' : ''} de donnees)
          </span>
        </div>
      )}
    </div>
  )
}
